import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { STORAGE_KEY_PENDING_ADVISOR_ALIAS } from '../const';
import {
    ADVISOR_LOCK_REASON,
    ZARA_ADVISOR_ALIAS,
    ZARA_TRIAL_MESSAGE_LIMIT,
    getAdvisorLockReason,
    isPremiumSubscriber,
    shouldShowZaraWelcome
} from '../service/advisorAccess';
import { store } from '../store';
import { getSeenAdvisorIntros, getUser, getUserZaraTrial, toggleAuthModal } from '../store/slices/authSlice';
import { openPaymentModal, setShowZaraUpsellModal, setShowZaraWelcomeModal } from '../store/slices/modalsSlice';

// HORO-10504: shared advisor-access gate for entry points that can start a
// chat with a specific advisor by ALIAS directly — a user landing on
// /advisor/:alias (typed, bookmarked, or shared), rather than tapping an
// already-gated catalog card or nav-menu row.
//
// MarketplaceActionButton and MenuContent.handleAdvisorTap already run this
// exact check before ever letting a click through to a real chat. This hook
// is the sibling for every place the chat page can start a chat with `alias`
// on their own — the landing-mode auto-create effect, HomeCards/cold-start
// card taps (wrapForLanding), and ChatBar's first landing-mode send — none
// of which ran any access check at all, so a direct URL visit (or a fast
// typed message before any redirect could fire) bypassed the catalog gate
// entirely, for every locked advisor, not just Zara.
//
// Mirrors useMessageLimitGate's shape exactly: reads a fresh state snapshot
// via `store.getState()` (correct regardless of how the caller memoises this
// callback), dispatches the appropriate modal, and returns a boolean the
// caller checks before proceeding — `true` to abort, `false` to continue.
export const useAdvisorLockGate = () => {
    const dispatch = useDispatch();

    return useCallback(
        (alias) => {
            if (!alias) return false;
            const state = store.getState();
            const user = getUser(state);
            const isPremiumUser = isPremiumSubscriber(user);
            // HORO-10599: same tri-state useZaraTrial computes, inlined here
            // since this runs from a plain callback (store.getState()
            // snapshot), not component render — can't call a hook.
            const zaraTrialExhausted = !isPremiumUser && (!user?.id || (getUserZaraTrial(state)?.used ?? 0) >= ZARA_TRIAL_MESSAGE_LIMIT);
            const lockReason = getAdvisorLockReason(alias, { isUser: !!user?.id, isPremiumUser, zaraTrialExhausted });

            if (lockReason === ADVISOR_LOCK_REASON.REGISTER) {
                // HORO-10599: Zara's REGISTER case opens her own modal
                // instead of the generic register flow — mirrors
                // MarketplaceActionButton's identical special-case.
                if (alias === ZARA_ADVISOR_ALIAS) {
                    dispatch(setShowZaraUpsellModal(true));
                    return true;
                }
                if (typeof sessionStorage !== 'undefined') {
                    sessionStorage.setItem(STORAGE_KEY_PENDING_ADVISOR_ALIAS, alias);
                }
                dispatch(toggleAuthModal({ trigger: true, step: 1, context: 'advisorLocked' }));
                return true;
            }
            if (lockReason === ADVISOR_LOCK_REASON.SUBSCRIBE) {
                // HORO-10562: same generalization as MarketplaceActionButton
                // — Zara keeps her bespoke modal, every other now-locked
                // advisor opens the generic subscribe flow instead.
                //
                // HORO-10563: PaymentModal itself now branches on whether
                // the viewer is signed in — see its own comment for why.
                if (alias === ZARA_ADVISOR_ALIAS) {
                    dispatch(setShowZaraUpsellModal(true));
                } else {
                    dispatch(openPaymentModal({ source: 'advisorLocked', advisorAlias: alias }));
                }
                return true;
            }

            // Unlocked, but this account hasn't been through this advisor's
            // one-time welcome yet (currently only Zara has one) — same
            // check MarketplaceActionButton/MenuContent already run before
            // letting an unlocked tap through to a real chat.
            const seenAdvisorIntros = getSeenAdvisorIntros(state);
            if (shouldShowZaraWelcome(alias, { isPremiumUser, seenAdvisorIntros })) {
                dispatch(setShowZaraWelcomeModal(true));
                return true;
            }

            return false;
        },
        [dispatch]
    );
};

export default useAdvisorLockGate;
